import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Clapperboard, LayoutGrid, List, Network } from "lucide-react";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { Button } from "@web/components/ui/button.tsx";
import { QueryErrorState } from "@web/features/diagnostics/QueryErrorState.tsx";
import { useGlobeData } from "@web/features/globe/api.ts";
import { enqueueShotArtists } from "@web/features/globe/enqueueShotArtists.ts";
import { ArtistGraphMap } from "@web/features/library/ArtistGraphMap.tsx";
import {
  useArtists,
  useGenresFlat,
  type ArtistCollabItem,
  type ArtistListItem,
} from "@web/features/library/api.ts";
import { FilterBar } from "@web/features/library/FilterBar.tsx";
import {
  expandGenreSubtree,
  formatIdList,
  parseIdList,
} from "@web/features/library/filterUrl.ts";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { cn } from "@web/lib/utils.ts";
import { useGraphPathStore } from "@web/stores/graphPath.ts";
import { useUiStore } from "@web/stores/ui.ts";

const ROW_HEIGHT = 44;

function genrePreview(genres: string[] | null | undefined): string {
  if (!genres || genres.length === 0) return "—";
  return genres.slice(0, 2).join(", ") + (genres.length > 2 ? ` +${genres.length - 2}` : "");
}

let measureCanvas: HTMLCanvasElement | null = null;

function measureNameWidth(text: string, font: string): number {
  if (typeof document === "undefined") return text.length * 7;
  if (!measureCanvas) measureCanvas = document.createElement("canvas");
  const ctx = measureCanvas.getContext("2d");
  if (!ctx) return text.length * 7;
  ctx.font = font;
  return ctx.measureText(text).width;
}

/** Pack names to column width. Shared width from one observer — no per-row RO. */
function AlsoWithCell({
  artist,
  maxWidth,
  onOpenCollab,
  onShowAll,
}: {
  artist: ArtistListItem;
  maxWidth: number;
  onOpenCollab: (collab: ArtistCollabItem) => void;
  onShowAll: () => void;
}) {
  if (artist.collabs.length === 0) {
    return <span className="text-faint">—</span>;
  }

  const font = "500 12px ui-sans-serif, system-ui, sans-serif";
  const gap = 4;
  const dotW = measureNameWidth("·", font) + gap;
  const pad = 4;

  let visible = 0;
  if (maxWidth > 0) {
    let used = 0;
    for (let i = 0; i < artist.collabs.length; i++) {
      const name = artist.collabs[i]?.name ?? "Unknown";
      const nameW = Math.min(measureNameWidth(name, font), 160);
      const sep = i === 0 ? 0 : gap + dotW;
      const remaining = artist.collab_total - (i + 1);
      const moreLabel = remaining > 0 ? `+${remaining}` : "";
      const moreW = moreLabel ? gap + measureNameWidth(moreLabel, font) : 0;
      if (used + sep + nameW + moreW + pad > maxWidth) break;
      used += sep + nameW;
      visible = i + 1;
    }
    if (visible === 0) visible = 1;
  } else {
    visible = Math.min(3, artist.collabs.length);
  }

  const shown = artist.collabs.slice(0, visible);
  const more = Math.max(0, artist.collab_total - shown.length);

  return (
    <span className="flex min-w-0 items-center gap-x-1 overflow-hidden whitespace-nowrap text-xs">
      {shown.map((c, i) => (
        <span key={c.id} className="inline-flex min-w-0 shrink items-center">
          {i > 0 && <span className="mr-1 shrink-0 text-faint">·</span>}
          <button
            type="button"
            className="max-w-40 truncate text-accent hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              onOpenCollab(c);
            }}
          >
            {c.name ?? "Unknown"}
          </button>
        </span>
      ))}
      {more > 0 && (
        <button
          type="button"
          className="shrink-0 text-faint hover:text-text"
          onClick={(e) => {
            e.stopPropagation();
            onShowAll();
          }}
        >
          +{more}
        </button>
      )}
    </span>
  );
}

function ArtistsTable({
  rows,
  selectedIds,
  onToggleSelected,
  onToggleAll,
  onOpen,
  onExplore,
  onOpenCollab,
  onOpenTracks,
  onOpenAlbums,
}: {
  rows: ArtistListItem[];
  selectedIds: Set<string>;
  onToggleSelected: (id: string) => void;
  onToggleAll: () => void;
  onOpen: (artist: ArtistListItem, focusCollaborators?: boolean) => void;
  onExplore: (artist: ArtistListItem) => void;
  onOpenCollab: (artist: ArtistListItem, collab: ArtistCollabItem) => void;
  onOpenTracks: (artist: ArtistListItem) => void;
  onOpenAlbums: (artist: ArtistListItem) => void;
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const alsoColRef = useRef<HTMLDivElement>(null);
  const [alsoColWidth, setAlsoColWidth] = useState(0);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 16,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));

  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const el = alsoColRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? el.clientWidth;
      setAlsoColWidth(Math.max(0, w - 24)); // minus cell px-3 padding
    });
    ro.observe(el);
    setAlsoColWidth(Math.max(0, el.clientWidth - 24));
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={parentRef}
      className="h-full min-h-0 overflow-auto rounded-lg border border-line"
    >
      <div className="sticky top-0 z-10 grid min-w-[48rem] grid-cols-[2rem_18rem_5rem_5rem_11rem_minmax(12rem,1fr)] border-b border-line bg-panel text-left text-xs tracking-wider text-faint uppercase">
        <div className="flex items-center justify-center px-1 py-2">
          <input
            type="checkbox"
            checked={allSelected}
            aria-label="Select all visible artists"
            className="size-3.5 accent-accent"
            onChange={() => onToggleAll()}
          />
        </div>
        <div className="px-3 py-2 font-semibold">Artist</div>
        <div className="px-3 py-2 text-right font-semibold">Tracks</div>
        <div className="px-3 py-2 text-right font-semibold">Albums</div>
        <div className="px-3 py-2 font-semibold">Genres</div>
        <div ref={alsoColRef} className="px-3 py-2 font-semibold">
          Also with
        </div>
      </div>
      <div className="relative min-w-[48rem]" style={{ height: totalSize }}>
        {virtualRows.map((vr) => {
          const artist = rows[vr.index];
          if (!artist) return null;
          const selected = selectedIds.has(artist.id);
          return (
            <div
              key={artist.id}
              role="row"
              className={cn(
                "absolute right-0 left-0 grid cursor-pointer grid-cols-[2rem_18rem_5rem_5rem_11rem_minmax(12rem,1fr)] border-b border-line/60 text-sm hover:bg-row-hover",
                selected && "bg-row-selected",
              )}
              style={{ height: vr.size, transform: `translateY(${vr.start}px)` }}
              onClick={() => {
                if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
                clickTimerRef.current = setTimeout(() => {
                  clickTimerRef.current = null;
                  onOpen(artist);
                }, 250);
              }}
              onDoubleClick={(e) => {
                e.preventDefault();
                if (clickTimerRef.current) {
                  clearTimeout(clickTimerRef.current);
                  clickTimerRef.current = null;
                }
                onExplore(artist);
              }}
              title="Click for details · double-click to open in graph"
            >
              <div
                className="flex items-center justify-center px-1"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  aria-label={`Select ${artist.name ?? artist.id}`}
                  className="size-3.5 accent-accent"
                  onChange={() => onToggleSelected(artist.id)}
                />
              </div>
              <div className="flex min-w-0 items-center gap-3 px-3">
                <EntityAvatar src={artist.image_url} alt="" size="sm" rounded="full" />
                <span className="truncate font-medium">{artist.name ?? "Unknown"}</span>
              </div>
              <div className="flex items-center justify-end px-3">
                <button
                  type="button"
                  className="font-mono tabular-nums text-muted hover:text-accent hover:underline"
                  title="Open tracks in detail sheet"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenTracks(artist);
                  }}
                >
                  {artist.track_count.toLocaleString()}
                </button>
              </div>
              <div className="flex items-center justify-end px-3">
                <button
                  type="button"
                  className="font-mono tabular-nums text-muted hover:text-accent hover:underline"
                  title="Open albums in detail sheet"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenAlbums(artist);
                  }}
                >
                  {artist.album_count.toLocaleString()}
                </button>
              </div>
              <div className="flex items-center truncate px-3 text-muted">
                {genrePreview(artist.spotify_genres)}
              </div>
              <div
                className="flex min-w-0 items-center px-3"
                onClick={(e) => e.stopPropagation()}
              >
                <AlsoWithCell
                  artist={artist}
                  maxWidth={alsoColWidth}
                  onOpenCollab={(c) => onOpenCollab(artist, c)}
                  onShowAll={() => onOpen(artist, true)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ArtistsPage() {
  const artists = useArtists();
  const genres = useGenresFlat();
  const openDetail = useOpenDetail();
  const view = useUiStore((s) => s.artistsView);
  const setView = useUiStore((s) => s.setArtistsView);
  const resetGraph = useGraphPathStore((s) => s.resetTo);
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = searchParams.get("q") ?? "";
  const genreIds = useMemo(
    () => parseIdList(searchParams.get("genre")),
    [searchParams],
  );
  const [draftQ, setDraftQ] = useState(qParam);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const globe = useGlobeData();

  useEffect(() => {
    setDraftQ(qParam);
  }, [qParam]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (draftQ.trim()) next.set("q", draftQ.trim());
      else next.delete("q");
      if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    }, 200);
    return () => window.clearTimeout(handle);
  }, [draftQ]); // eslint-disable-line react-hooks/exhaustive-deps

  const genreOptions = useMemo(
    () => (genres.data ?? []).map((g) => ({ id: g.id, label: g.name })),
    [genres.data],
  );

  const genreMatch = useMemo(() => {
    if (genreIds.length === 0) return null;
    return expandGenreSubtree(genreIds, genres.data ?? []);
  }, [genreIds, genres.data]);

  const filtered = useMemo(() => {
    const term = qParam.trim().toLowerCase();
    const list = artists.data ?? [];
    return list.filter((a) => {
      if (term && !(a.name ?? "").toLowerCase().includes(term)) return false;
      if (genreMatch) {
        const ids = a.genre_ids ?? [];
        if (!ids.some((id) => genreMatch.has(id))) return false;
      }
      return true;
    });
  }, [artists.data, qParam, genreMatch]);

  function setGenreIds(ids: string[]) {
    const next = new URLSearchParams(searchParams);
    const formatted = formatIdList(ids);
    if (formatted) next.set("genre", formatted);
    else next.delete("genre");
    setSearchParams(next, { replace: true });
  }

  function clearFilters() {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    next.delete("genre");
    setDraftQ("");
    setSearchParams(next, { replace: true });
  }

  function openArtist(artist: ArtistListItem, focusCollaborators = false) {
    openDetail(
      { type: "artist", id: artist.id, label: artist.name ?? "Artist" },
      {
        mode: "reset",
        ...(focusCollaborators ? { artistFocus: "collabs" as const } : {}),
      },
    );
  }

  function openArtistTracks(artist: ArtistListItem) {
    openDetail(
      { type: "artist", id: artist.id, label: artist.name ?? "Artist" },
      { mode: "reset", artistFocus: "tracks" },
    );
  }

  function openArtistAlbums(artist: ArtistListItem) {
    openDetail(
      { type: "artist", id: artist.id, label: artist.name ?? "Artist" },
      { mode: "reset", artistFocus: "albums" },
    );
  }

  function exploreInGraph(artist: ArtistListItem) {
    resetGraph({ type: "artist", id: artist.id, label: artist.name ?? "Artist" });
    setView("graph");
  }

  function openCollabFrom(artist: ArtistListItem, collab: ArtistCollabItem) {
    openDetail(
      { type: "artist", id: artist.id, label: artist.name ?? "Artist" },
      { mode: "reset" },
    );
    openDetail(
      { type: "artist", id: collab.id, label: collab.name ?? "Artist" },
      { mode: "push" },
    );
  }

  const isFiltered = Boolean(qParam.trim()) || genreIds.length > 0;

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 flex-col gap-3 border-b border-line px-5 py-4">
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold tracking-wider text-faint uppercase">Browse</p>
            <h1 className="text-2xl font-semibold tracking-tight">Artists</h1>
            <p className="mt-1 text-sm text-muted">
              {artists.isLoading
                ? "Loading…"
                : `${filtered.length.toLocaleString()} of ${(artists.data?.length ?? 0).toLocaleString()} in your library`}
            </p>
          </div>
          <div
            role="group"
            aria-label="View mode"
            className="flex rounded-md border border-line p-0.5"
          >
            <button
              type="button"
              aria-pressed={view === "table"}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-semibold",
                view === "table" ? "bg-row-selected text-text" : "text-muted hover:text-text",
              )}
              onClick={() => setView("table")}
            >
              <List className="size-3.5" />
              Table
            </button>
            <button
              type="button"
              aria-pressed={view === "explore"}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-semibold",
                view === "explore" ? "bg-row-selected text-text" : "text-muted hover:text-text",
              )}
              onClick={() => setView("explore")}
            >
              <LayoutGrid className="size-3.5" />
              Explore
            </button>
            <button
              type="button"
              aria-pressed={view === "graph"}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-semibold",
                view === "graph" ? "bg-row-selected text-text" : "text-muted hover:text-text",
              )}
              onClick={() => setView("graph")}
            >
              <Network className="size-3.5" />
              Graph
            </button>
          </div>
        </div>

        <FilterBar
          searchValue={draftQ}
          onSearchChange={setDraftQ}
          searchPlaceholder="Search artists in your library"
          genreOptions={genreOptions}
          genreIds={genreIds}
          onGenreIdsChange={setGenreIds}
          onClearAll={isFiltered ? clearFilters : undefined}
        />

        {view === "table" && selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted">
              {selectedIds.size} selected
            </span>
            <button
              type="button"
              disabled={!globe.data}
              className={cn(
                "inline-flex h-7 items-center gap-1.5 rounded-md border border-line px-2 text-xs font-semibold",
                "text-muted hover:border-accent/40 hover:text-text disabled:opacity-40",
              )}
              onClick={() => {
                if (!globe.data) return;
                const names = new Map<string, string>();
                for (const a of filtered) {
                  if (selectedIds.has(a.id)) {
                    names.set(a.id, a.name ?? a.id);
                  }
                }
                enqueueShotArtists([...selectedIds], names, globe.data.points);
              }}
            >
              <Clapperboard className="size-3.5" />
              Add selected to shot queue
            </button>
            <Link
              to="/globe"
              className="text-xs font-semibold text-faint hover:text-text"
            >
              Open globe
            </Link>
            <button
              type="button"
              className="text-xs text-faint hover:text-text"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear selection
            </button>
          </div>
        )}
      </header>

      <div
        className={cn(
          "min-h-0 flex-1 p-4",
          view === "graph" || view === "table"
            ? "flex flex-col overflow-hidden"
            : "overflow-y-auto",
        )}
      >
        {artists.isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 16 }, (_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        )}

        {artists.isError && (
          <QueryErrorState
            error={artists.error}
            onRetry={() => void artists.refetch()}
            actionLabel="load artists"
          />
        )}

        {artists.isSuccess && filtered.length === 0 && (
          <EmptyState
            title={isFiltered ? "No matches" : "No artists yet"}
            description={
              isFiltered
                ? "Try a different search or clear the filter."
                : "Import or sync a library first."
            }
          />
        )}

        {artists.isSuccess && (artists.data?.length ?? 0) > 0 && view === "graph" && (
          <ArtistGraphMap artists={artists.data ?? []} />
        )}

        {artists.isSuccess && filtered.length > 0 && view === "explore" && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-2">
            {filtered.map((artist) => (
              <div
                key={artist.id}
                className="group flex items-center rounded-md pr-1 hover:bg-row-hover"
              >
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2 text-left"
                  onClick={() => openArtist(artist)}
                >
                  <EntityAvatar src={artist.image_url} alt="" size="md" rounded="full" />
                  <div className="min-w-0">
                    <div className="truncate font-medium">{artist.name ?? "Unknown"}</div>
                    <div className="text-xs text-faint">
                      {artist.track_count.toLocaleString()} tracks ·{" "}
                      {artist.album_count.toLocaleString()} albums
                    </div>
                  </div>
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="opacity-0 group-hover:opacity-100"
                  onClick={() => exploreInGraph(artist)}
                >
                  Graph
                </Button>
                <Button variant="ghost" size="sm" asChild className="opacity-0 group-hover:opacity-100">
                  <Link to={`/all?artist=${encodeURIComponent(artist.id)}`}>Tracks</Link>
                </Button>
              </div>
            ))}
          </div>
        )}

        {artists.isSuccess && filtered.length > 0 && view === "table" && (
          <ArtistsTable
            rows={filtered}
            selectedIds={selectedIds}
            onToggleSelected={(id) => {
              setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              });
            }}
            onToggleAll={() => {
              setSelectedIds((prev) => {
                const allOn = filtered.every((r) => prev.has(r.id));
                if (allOn) return new Set();
                return new Set(filtered.map((r) => r.id));
              });
            }}
            onOpen={openArtist}
            onExplore={exploreInGraph}
            onOpenCollab={openCollabFrom}
            onOpenTracks={openArtistTracks}
            onOpenAlbums={openArtistAlbums}
          />
        )}
      </div>
    </div>
  );
}
